import { all, call, put, select, delay, takeLatest } from "redux-saga/effects";
import { toast } from "react-toastify";
import { normalize, schema } from "normalizr";
import { getErrorMessage } from "utils/sagasErrorHandler";
import { ITEMS_PER_PAGE } from "constants/general";
import { openUserAction } from "services/UserActionConfirm/actions";
import { store } from "index";
import { USER_ACTION_DIALOG_TYPE } from "services/UserActionConfirm/constants";
import { dateTimeFormatterISOString } from "utils";
import messageHelper from "utils/messages/messageHelper";

import {
    changeEnabledTask,
    enableInitialScrapeTasks,
    enableInitialScrapeTasksError,
    enableInitialScrapeTasksSuccess,
    getInitialScrapeTasks,
    getInitialScrapeTasksError,
    getInitialScrapeTasksSuccess,
    setStartTasksDialog,
    startTasks,
    mergeStartTasksDialog,
    createInitialScrapeTask,
    setInitialScrapeTaskDialog,
    mergeInitialScrapeTaskDialog,
    oneTimeSearch,
    mergeOneTimeSearchDialog,
    setOneTimeSearchDialog,
    intervalHours,
    mergeIntervalHoursTasksDialog,
    deleteTasks,
    reschedule,
    mergeRescheduleDialog,
    getCountSchedulerList,
    getCountSchedulerListError,
    getCountSchedulerListSuccess,
    changeAllEnableStatus,
    updateAllByRequest,
    downloadKeywords,
    mergeDownloadKeywordsDialog,
    setIntervalHoursTasksDialog,
    paginationLimit,
    mergePaginationLimitTasksDialog,
    setPaginationLimitTasksDialog,
    mergeDeleteDialog,
    mergeProgressDialog,
    setRescheduleDialog,
    mergeEditMetadataDialog,
    setEditMetadataDialog,
    editMetadata,
} from "modules/Scheduler/services/InitialScrapeTasks/actions";
import {
    createInitialScrapeTaskRequest,
    getInitialScrapeTaskRequest,
    handlePatchTaskRequest,
    handleRunTaskRequest,
    oneTimeSearchRequest,
    reschedulePidRequest,
    pidProcessRequest,
    bulkUpdateRequest,
    getCountSchedulerListRequest,
    updateAllByRequestRequest,
    exportInitialScrapeTaskRequest,
    deleteInitialScrapeTasksRequest,
    partialUpdateMetadataRequest,
    exportInitialScrapeTaskResultRequest
} from "modules/Scheduler/services/InitialScrapeTasks/api";
import {
    downloadDialogSelector,
    initialScrapeTasksSelector,
    selectedInitialScrapeTasksSelector,
    startTasksDialogSelector,
    deleteDialogSelector,
    progressDialogSelector,
    oneTimeSearchDialogSelector
} from "modules/Scheduler/services/InitialScrapeTasks/selectors";
import {sidParamsSelector} from "modules/App/state/selectors";

const initialScrapeTasksSchema = new schema.Entity("initial_scrape_tasks");
const initialScrapeTasksListSchema = new schema.Array(initialScrapeTasksSchema);

//Workers
function* handleRefreshInitialScrapeTasks() {
    const sidParams = yield select(sidParamsSelector);
    yield put(getInitialScrapeTasks({ limit: ITEMS_PER_PAGE, ...sidParams }));
}

function* handleGetInitialScrapeTasks ({ payload }) {
    try {
        const data = yield call(getInitialScrapeTaskRequest, payload);

        const { result, entities } = normalize(data.initial_scrape_tasks, initialScrapeTasksListSchema);
        yield put(getInitialScrapeTasksSuccess({
            initialScrapeTasks: entities.initial_scrape_tasks,
            ids: result
        }));
    } catch (error) {
        yield put(getInitialScrapeTasksError());
        getErrorMessage(error);
    }
}

function* handleBulkUpdate (payload) {
    const { initialScrapeTasks, selectedInitialScrapeTasks } = yield select(initialScrapeTasksSelector);

    const objectUpdate = selectedInitialScrapeTasks.map((selectedId) => {
        const newTask = { ...initialScrapeTasks[selectedId] };

        Object.keys(payload).forEach((key, i) => {
            newTask[key] = payload[key];
        });

        return newTask;
    });

    yield call(bulkUpdateRequest, { initial_scrape_tasks: objectUpdate });
}

function* handleEnableTasks ({ payload }) {
    try {
        const { selectedInitialScrapeTasks } = yield select(initialScrapeTasksSelector);
        yield call(handleBulkUpdate, payload);
        yield call(
            toast.success,
            `The Task${selectedInitialScrapeTasks.length > 1 ? "s" : ""} with id - 
            ${selectedInitialScrapeTasks.join(", ")} has been successfully ${payload ? "enabled" : "disabled"}`
        );
        yield handleRefreshInitialScrapeTasks();
        yield put(enableInitialScrapeTasksSuccess());
    } catch (error) {
        yield put(enableInitialScrapeTasksError());
        getErrorMessage(error);
    }
}

function* handleEnableChangeTask ({ payload }) {
    try {
        yield call(handlePatchTaskRequest, payload);
        yield handleRefreshInitialScrapeTasks();
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleStartTasks ({ payload }) {
    try {
        yield put(mergeStartTasksDialog({ isLoading: true }));
        const { selected } = yield select(startTasksDialogSelector);
        yield all(selected.map((id) => call(handleRunTaskRequest, { id, body: payload })));
        yield put(setStartTasksDialog());
        yield handleRefreshInitialScrapeTasks();
    } catch (error) {
        yield put(mergeStartTasksDialog({ isLoading: false }));
        getErrorMessage(error);
    }
}

function* handleCreateInitialScrapeTask ({ payload }) {
    try {
        yield put(mergeInitialScrapeTaskDialog({ isLoading: true }));
        yield call(createInitialScrapeTaskRequest, payload);
        yield put(setInitialScrapeTaskDialog());
        yield handleRefreshInitialScrapeTasks();
    } catch (error) {
        yield put(mergeInitialScrapeTaskDialog({ isLoading: false }));
        getErrorMessage(error);
    }
}

function* handleOneTimeSearch ({ payload }) {
    try {
        const { browsers, is_ab_test, ...restParams } = payload;
        const newBrowsers = (is_ab_test && browsers && browsers.length > 0) ? browsers : [{manual_browser_source_type_code: "", manual_browser_source_credentials_id: ""}];
        const { isRescrape } = yield select(oneTimeSearchDialogSelector);
        yield put(mergeOneTimeSearchDialog({isLoading: true}));
        yield all(newBrowsers.map(ab_test => call(oneTimeSearchRequest, {
            ab_test,
            ...restParams,
        })));
        yield put(setOneTimeSearchDialog());
        if (isRescrape) {
            yield call(toast.success, "The task has been successfully sent to rescrape");
        } else {
            yield handleRefreshInitialScrapeTasks();
        }
    } catch (error) {
        yield put(mergeOneTimeSearchDialog({ isLoading: false }));
        getErrorMessage(error);
    }
}

function* handleIntervalHours ( { payload } ) {
    try {
        yield put( mergeIntervalHoursTasksDialog( { isLoading: true } ) );
        const selectedInitialScrapeTasks = yield select( selectedInitialScrapeTasksSelector );
        yield call( handleBulkUpdate, payload );
        yield call(
            toast.success,
            `The Task${selectedInitialScrapeTasks.length > 1 ? "s" : ""} with id${selectedInitialScrapeTasks.length > 1 ? "s" : ""} - ${selectedInitialScrapeTasks.join( ", " )} has been successfully updated`
        );
        yield put( setIntervalHoursTasksDialog() );
        yield handleRefreshInitialScrapeTasks();
    } catch ( error ) {
        yield put( mergeIntervalHoursTasksDialog( { isLoading: false } ) );
        getErrorMessage(error);
    }
}

function* handleEditMetadata ( { payload } ) {
    try {
        yield put( mergeEditMetadataDialog( { isLoading: true } ) );
        const {
            selectedInitialScrapeTasks,
            initialScrapeTasks,
        } = yield select( initialScrapeTasksSelector );

        yield all(selectedInitialScrapeTasks.map((id) => call(partialUpdateMetadataRequest, {
            receiver_code: initialScrapeTasks[id].receiver_code,
            brand_code: initialScrapeTasks[id].brand_code,
            branch_code: initialScrapeTasks[id].branch_code,
            keyword: initialScrapeTasks[id].keyword,
            url: initialScrapeTasks[id].url,
            metadata_properties: payload?.metadata?.metadata_property ? payload.metadata.metadata_property : [],
        })));
        yield call(
            toast.success,
            `The Task${selectedInitialScrapeTasks.length > 1 ? "s" : ""} with id${selectedInitialScrapeTasks.length > 1 ? "s" : ""} - ${selectedInitialScrapeTasks.join( ", " )} has been successfully updated`
        );
        yield put( setEditMetadataDialog() );
        yield handleRefreshInitialScrapeTasks();
    } catch ( error ) {
        yield put( mergeEditMetadataDialog( { isLoading: false } ) );
        getErrorMessage(error);
    }
}

function* handlePaginationLimit ( { payload } ) {
    try {
        yield put( mergePaginationLimitTasksDialog( { isLoading: true } ) );
        const selectedInitialScrapeTasks = yield select( selectedInitialScrapeTasksSelector );
        yield call( handleBulkUpdate, payload );
        yield call(
            toast.success,
            `The Task${selectedInitialScrapeTasks.length > 1 ? "s" : ""} with id${selectedInitialScrapeTasks.length > 1 ? "s" : ""} - ${selectedInitialScrapeTasks.join( ", " )} has been successfully updated`
        );
        yield put( setPaginationLimitTasksDialog() );
        yield handleRefreshInitialScrapeTasks();
    } catch ( error ) {
        yield put( mergePaginationLimitTasksDialog( { isLoading: false } ) );
        getErrorMessage(error);
    }
}

function* handleReschedule ({ payload }) {
    try {
        const searchParams = { ...payload };
        delete searchParams.offset;
        delete searchParams.limit;

        const { pid } = yield call(reschedulePidRequest, searchParams);
        yield put(setRescheduleDialog());
        yield put(mergeProgressDialog({ isOpenProgress: true, isLoadingProgress: true }));
        yield handleProgress(pid);
    } catch (error) {
        getErrorMessage(error);
        yield put(mergeRescheduleDialog({ isLoading: false }));
    }
}

function* handleDeleteTasks ({ payload }) {
    try {
        const searchParams = { ...payload };
        delete searchParams.take;
        delete searchParams.offset;
        delete searchParams.limit;

        //deleting by one or multiple selected by checkboxes
        if (searchParams?.ids && searchParams.ids.length > 0) {
            yield call(deleteInitialScrapeTasksRequest, searchParams);
            yield delay(2000);
            yield handleRefreshInitialScrapeTasks();
            const ids = searchParams?.ids ? searchParams?.ids : [];
            messageHelper({
                type: "success",
                actionSubject: `Task${ids.length > 1 ? "s" : ""} with id${ids.length > 1 ? "s" : ""} - ${ids.join(", ")}`,
                action: "deleted",
                plural: ids.length > 1
            });
        } else {
            //delete many by request
            const { pid } = yield call(deleteInitialScrapeTasksRequest, searchParams);
            yield handleCheckDeleteAll(pid);
        }
    } catch (error) {
        getErrorMessage(error);
        yield put(mergeDeleteDialog({ isLoading: false }));
    }
}

function* handleCheckDeleteAll (pid) {
    try {
        const { isOpen } = yield select(deleteDialogSelector);

        const value = yield call(pidProcessRequest, pid);
        const status = Number((value * 100).toFixed());
        yield put(mergeDeleteDialog({ isLoading: true, status }));

        if(status !== 100 && isOpen){
            yield delay(1500);
            yield handleCheckDeleteAll(pid);
        } else {
            yield handleRefreshInitialScrapeTasks();
            yield put(mergeDeleteDialog({isLoading: false}));
            messageHelper({
                type: "success",
                actionSubject: `filtered initial scrape tasks`,
                action: "deleted",
                plural: true
            });
        }
    } catch (error) {
        getErrorMessage(error);
        yield put(mergeDeleteDialog({ isLoading: false }));
    }
}

function* handleGetCountSchedulerList ({ payload }) {
    try {
        const { count } = yield call(getCountSchedulerListRequest, payload);
        yield put(getCountSchedulerListSuccess(count));
    } catch (error) {
        getErrorMessage(error);
        yield put(getCountSchedulerListError());
    }
}

function* handleUpdateAllByRequest ({ payload }) {
    try {
        const request = { ...payload };
        delete request.filterParams.offset;
        delete request.filterParams.limit;

        const { pid } = yield call(updateAllByRequestRequest, request);
        yield put(mergeProgressDialog({ isOpenProgress: true }));
        yield handleProgress(pid);
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleProgress (pid) {
    try {
        const { isOpenProgress } = yield select(progressDialogSelector);

        const value = yield call(pidProcessRequest, pid);
        const status = Number((value * 100).toFixed());
        yield put(mergeProgressDialog({ isLoadingProgress: true, statusProgress: status }));

        if(status !== 100 && isOpenProgress){
            yield delay(1500);
            yield handleProgress(pid);
        } else {
            yield handleRefreshInitialScrapeTasks();
            yield put(mergeProgressDialog({ isLoadingProgress: false }));
        }
    } catch (error) {
        getErrorMessage(error);
        yield put(mergeProgressDialog({ isLoadingProgress: false }));
    }
}

function* handleChangeAllEnableStatus ({ payload }) {
    try {
        const { searchParams } = payload;
        const status = payload.data.enabled;
        const { last_start_at_from, last_start_at_to, next_start_at_from, next_start_at_to, ...rest } = searchParams;
        const newValues = { ...rest };

        if(last_start_at_from) {
            newValues.last_start_at_from = dateTimeFormatterISOString(last_start_at_from);
        }

        if(last_start_at_to) {
            newValues.last_start_at_to = dateTimeFormatterISOString(last_start_at_to);
        }

        if(next_start_at_from) {
            newValues.next_start_at_from = dateTimeFormatterISOString(next_start_at_from);
        }

        if(next_start_at_to) {
            newValues.next_start_at_to = dateTimeFormatterISOString(next_start_at_to);
        }

        const nextStartParams = {
            ...newValues,
            next_start_at_to: dateTimeFormatterISOString(new Date())
        };

        const [
            currentCount,
            nextStartCount
        ] = yield all([
            call(getCountSchedulerListRequest, searchParams),
            call(getCountSchedulerListRequest, nextStartParams)
        ]);

        const isErrorType = nextStartCount?.count >= 1000 && status;

        const message = isErrorType ?
            `${nextStartCount.count} - keyword${currentCount.count > 1 ? "s" : ""} are not rescheduled. Please reschedule keyword${currentCount.count > 1 ? "s" : ""} before enabling` :
            `Please confirm ${status ? "enabling" : "disabling"} for ${currentCount.count} initial scrape task${currentCount.count > 1 ? "s" : ""}`;

        yield put(openUserAction({
            dialogType: isErrorType ? USER_ACTION_DIALOG_TYPE.Error : USER_ACTION_DIALOG_TYPE.Message,
            actionType: `${status ? "enable" : "disable"} initial scrape task${currentCount.count > 1 ? "s" : ""}`,
            message,
            actionsProps: { proceedButtonProps: { proceedText: status ? "Enable" : "Disable" } },
            action: () => store.dispatch(updateAllByRequest({ filterParams: searchParams, data: payload.data }))
        }));
    } catch (error) {
        getErrorMessage(error);
    }
}

function* getAllKeywords (pid, n) {
    try {
        const { isOpen } = yield select(downloadDialogSelector);

        const value = yield call(pidProcessRequest, pid);
        const status = Number((value * 100).toFixed());
        yield put(mergeDownloadKeywordsDialog({ isLoading: true, status }));
        if (status >= 99) {
            yield delay(4000);
        }
        if (status !== 100 && isOpen) {
            if (n <= 5) {
                yield delay(1500);
            } else {
                yield delay(4000);
            }
            yield getAllKeywords(pid, n + 1);
        } else {
            yield getExportKeywordsResult(pid)
        }
    } catch (error) {
        getErrorMessage(error);
        yield put(mergeDownloadKeywordsDialog({ isLoading: false }));
    }
}

function* getExportKeywordsResult (pid) {
    try {
        const { url, error } = yield call(exportInitialScrapeTaskResultRequest, pid);
        if (error && error.length > 0) {
            yield put(mergeDownloadKeywordsDialog({ isOpen: false, isLoading: false, status: null }));
            toast.error(error);
        } else {
            yield delay(3000);
            yield put(mergeDownloadKeywordsDialog({ isLoading: false, status: null, url }));
        }
    } catch (error) {
        getErrorMessage(error);
        yield put(mergeDownloadKeywordsDialog({ isLoading: false }));
    }
}

function* handleDownloadKeywords ({ payload }) {
    try {
        yield put(mergeDownloadKeywordsDialog({ isOpen: true, isLoading: true, status: 0, url: "" }));
        const { pid } = yield call(exportInitialScrapeTaskRequest, payload);
        yield put(mergeDownloadKeywordsDialog({ isOpen: true, isLoading: true, status: 0 }));

        yield getAllKeywords(pid, 1);
    } catch (error) {
        getErrorMessage(error);
    }
}

// Watchers
export function* schedulersEntriesSagas() {
    yield all([
        yield takeLatest(getInitialScrapeTasks, handleGetInitialScrapeTasks),
        yield takeLatest(enableInitialScrapeTasks, handleEnableTasks),
        yield takeLatest(changeEnabledTask, handleEnableChangeTask),
        yield takeLatest(startTasks, handleStartTasks),
        yield takeLatest(createInitialScrapeTask, handleCreateInitialScrapeTask),
        yield takeLatest(intervalHours, handleIntervalHours),
        yield takeLatest(editMetadata, handleEditMetadata),
        yield takeLatest(paginationLimit, handlePaginationLimit),
        yield takeLatest(oneTimeSearch, handleOneTimeSearch),
        yield takeLatest(deleteTasks, handleDeleteTasks),
        yield takeLatest(reschedule, handleReschedule),
        yield takeLatest(getCountSchedulerList, handleGetCountSchedulerList),
        yield takeLatest(changeAllEnableStatus, handleChangeAllEnableStatus),
        yield takeLatest(updateAllByRequest, handleUpdateAllByRequest),
        yield takeLatest(downloadKeywords, handleDownloadKeywords)
    ]);
}
